<template>
	<!-- Modal Overlay -->
	<div
		v-if="isOpen"
		class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4"
	>
		<!-- Modal Content -->
		<div
			class="bg-white rounded-2xl shadow-xl max-w-4xl w-full mx-auto max-h-[90vh] overflow-y-auto animate-fade-in-up"
		>
			<div class="relative p-6 sm:p-8">
				<!-- Close Button -->
				<button
					@click="$emit('close')"
					class="absolute top-4 right-4 text-gray-500 hover:text-gray-700 focus:outline-none"
				>
					<svg
						class="w-6 h-6"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M6 18L18 6M6 6l12 12"
						/>
					</svg>
				</button>

				<!-- Review Header -->
				<div class="text-center mb-6 sm:mb-8">
					<div
						class="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 bg-blue-600 rounded-full shadow-lg mb-3 sm:mb-4 animate-bounce-once"
					>
						<svg
							class="w-8 h-8 sm:w-10 sm:h-10 text-white"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
							></path>
						</svg>
					</div>
					<h1
						class="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-2"
					>
						Review Your Policy
					</h1>
					<p class="text-base sm:text-lg text-blue-600 font-medium">
						Share your feedback with us
					</p>
				</div>

				<!-- Review Card Content -->
				<div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
					<!-- Product Summary -->
					<div
						class="space-y-4"
						v-for="product in selectedProduct.product_details"
					>
						<h2
							class="text-lg sm:text-xl font-semibold text-gray-900"
						>
							Policy Details
						</h2>
						<div class="space-y-3">
							<div
								class="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
							>
								<div
									class="w-2 h-2 bg-blue-500 rounded-full mt-2"
								></div>
								<div class="flex-1">
									<p
										class="text-xs font-medium text-gray-500 uppercase tracking-wide"
									>
										Product
									</p>
									<p
										class="text-sm font-semibold text-gray-900"
									>
										{{
											product
												? product.productName
												: 'N/A'
										}}
									</p>
								</div>
							</div>
							<div
								class="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
							>
								<div
									class="w-2 h-2 bg-green-500 rounded-full mt-2"
								></div>
								<div class="flex-1">
									<p
										class="text-xs font-medium text-gray-500 uppercase tracking-wide"
									>
										Premium
									</p>
									<p
										class="text-sm font-semibold text-gray-900"
									>
										KES
										{{
											(selectedProduct.quotationPremium &&
												selectedProduct.quotationPremium
													.totalPremium) ||
											'N/A'
										}}
									</p>
								</div>
							</div>
							<div
								class="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
							>
								<div
									class="w-2 h-2 bg-purple-500 rounded-full mt-2"
								></div>
								<div class="flex-1">
									<p
										class="text-xs font-medium text-gray-500 uppercase tracking-wide"
									>
										Cover Type
									</p>
									<!-- <p class="text-sm font-semibold text-gray-900">{{ selectedCoverType ? selectedCoverType.charAt(0).toUpperCase() + selectedCoverType.slice(1) : 'N/A' }}</p> -->
								</div>
							</div>
						</div>
					</div>

					<!-- Review Form -->
					<div class="space-y-4">
						<h2
							class="text-lg sm:text-xl font-semibold text-gray-900"
						>
							Your Feedback
						</h2>
						<div class="flex justify-center space-x-1">
							<button
								v-for="star in 5"
								:key="star"
								@click="setRating(star)"
								@mouseenter="hoverRating = star"
								@mouseleave="hoverRating = 0"
								class="focus:outline-none"
							>
								<svg
									class="w-8 h-8 sm:w-10 sm:h-10 transition-colors duration-200"
									:class="{
										'text-blue-600':
											star <= (hoverRating || rating),
										'text-gray-300':
											star > (hoverRating || rating),
									}"
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path
										d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"
									></path>
								</svg>
							</button>
						</div>
						<textarea
							v-model="message"
							placeholder="Share your thoughts about our service..."
							class="w-full h-32 p-4 text-sm text-gray-900 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
						></textarea>
						<button
							@click="submitReview"
							class="w-full inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
						>
							Submit Review
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { submitReviewApi } from '../../../../newportal/api/portal';

export default {
	name: 'ReviewModal',
	props: {
		isOpen: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			rating: 0,
			hoverRating: 0,
			message: '',
		};
	},
	computed: {
		...mapGetters('quotation', ['selectCalculatedQuote']),
		...mapGetters('product', ['selectedProduct', 'selectedCoverType']),
	},
	methods: {
		setRating(value) {
			this.rating = value;
		},
		async submitReview() {
			if (this.rating === 0) {
				alert('Please select a star rating');
				return;
			}
			const payload = {
				productSystemRef:
					this.selectedProduct.product_details[0].productSystemRef,
				rate: this.rating,
				review: this.message,
			};
			try {
				const res = await submitReviewApi(payload);
				if (res.status) {
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Review Submitted',
						message: 'Your review has been successfully submitted.',
						duration: 3000,
					});
				}
			} catch (error) {
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error ',
					message: `Error submitting review`,
					duration: 4000,
				});
			}
			// Emit event to parent component to handle submission
			this.$emit('submit', {
				rating: this.rating,
				message: this.message,
			});
			this.rating = 0;
			this.message = '';
			this.$emit('close');
		},
	},
	mounted() {},
};
</script>

<style scoped lang="less">
.animate-fade-in-up {
	animation: fade-in-up 0.6s ease-out forwards;
	opacity: 0;
}

.animate-bounce-once {
	animation: bounce-once 1s ease-in-out;
}

@keyframes fade-in-up {
	from {
		opacity: 0;
		transform: translateY(20px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes bounce-once {
	0%,
	20%,
	50%,
	80%,
	100% {
		transform: translateY(0);
	}
	40% {
		transform: translateY(-10px);
	}
	60% {
		transform: translateY(-5px);
	}
}

/* Responsive adjustments */
@media (max-width: 640px) {
	.grid-cols-1.lg\\:grid-cols-2 {
		grid-template-columns: 1fr;
	}

	.text-3xl.sm\\:text-4xl {
		font-size: 1.75rem;
		line-height: 2rem;
	}

	.p-6,
	.p-8 {
		padding: 1rem;
	}
}

/* Enhanced touch targets */
button,
a {
	touch-action: manipulation;
}

/* Custom scrollbar */
::-webkit-scrollbar {
	width: 6px;
}

::-webkit-scrollbar-track {
	background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
	background: #cbd5e1;
	border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
	background: #94a3b8;
}
</style>
